// Blog routes: paths, the post index, and related-post ranking.
//
// Mirrors conversionRoutes.js deliberately. Four things must agree on a public
// URL — the router, every internal link, the sitemap and the prerenderer — so
// the path is built in exactly one place here and imported everywhere else.
//
// This module deliberately does NOT import from conversionRoutes, even though
// SITE_ORIGIN lives there: conversionRoutes has to import getBlogPaths for the
// indexable set, and the two importing each other is a cycle that resolves to
// `undefined` at module-init time depending on which side loads first. The
// origin therefore comes from siteOrigin.js, which both sides import and which
// imports nothing.

import { SITE_ORIGIN } from './siteOrigin.js';
import { BLOG_POSTS, BLOG_CLUSTERS } from '../data/blog/index.js';

export { SITE_ORIGIN };

export const BLOG_BASE = '/blogs';

export const blogPath = (slug) => `${BLOG_BASE}/${slug}`;

export const blogUrl = (slug) => `${SITE_ORIGIN}${blogPath(slug)}`;

// Hero (1200x675) vs card thumbnail (600x338). Both are committed files under
// public/blog — see scripts/generate-blog-images.py and
// scripts/generate-blog-thumbnails.sh. Separate sizes because a listing shows
// up to 25 at ~480px wide, where the hero is 2.5x oversized.
export const heroPath = (slug) => `/blog/${slug}.webp`;
export const thumbPath = (slug) => `/blog/thumbs/${slug}.webp`;

// Posts in a stable, deliberate order: cluster by cluster, pillar first within
// each. Object key order in BLOG_POSTS is the authored order, so this is a
// filter rather than a sort — an alphabetical listing would bury the pillars.
export const getAllPosts = () =>
  BLOG_CLUSTERS.flatMap((cluster) =>
    Object.values(BLOG_POSTS)
      .filter((p) => p.cluster === cluster.id)
      .sort((a, b) => Number(Boolean(b.pillar)) - Number(Boolean(a.pillar)))
  );

export const getPost = (slug) => BLOG_POSTS[String(slug || '').toLowerCase()] || null;

export const getBlogSlugs = () => getAllPosts().map((p) => p.slug);

// Every blog URL that belongs in the sitemap: the hub plus one per post.
export const getBlogPaths = () => [BLOG_BASE, ...getBlogSlugs().map(blogPath)];

// Related posts, for in-article internal linking.
//
// Same reasoning as getRelatedConversions: internal links are the one
// link-building lever entirely within our control, and an article nobody links
// to is an orphan reachable only from the sitemap. Ranked so the links are
// genuinely useful rather than a footer dump — an explicit `related` list on the
// post wins (the author knows best), then the cluster's pillar, then shared
// keywords, then cluster siblings.
export const getRelatedPosts = (slug, limit = 3) => {
  const self = getPost(slug);
  if (!self) return [];

  const selfKeywords = new Set(self.keywords || []);
  const explicit = new Set(self.related || []);

  return getAllPosts()
    .filter((p) => p.slug !== self.slug)
    .map((p) => {
      let score = 0;
      if (explicit.has(p.slug)) score += 100;
      if (p.cluster === self.cluster && p.pillar) score += 40;
      const shared = (p.keywords || []).filter((k) => selfKeywords.has(k)).length;
      score += shared * 8;
      if (p.cluster === self.cluster) score += 10;
      return { post: p, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ post }) => ({
      slug: post.slug,
      path: blogPath(post.slug),
      title: post.title,
      description: post.description,
      // Carried so a related card can render its thumbnail; without it the
      // strip at the foot of a post would be the only text-only cards on the
      // site.
      hero: post.hero,
      pillar: post.pillar,
    }));
};
